import React, { useState, useEffect } from 'react';
import { CheckCircle, Info, TrendingUp, Users, AlertCircle, ArrowLeft, Award, BarChart3, DollarSign, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';
import ShareButton from './ShareButton';

const SignificanceCalculator = () => {
  const [activeTab, setActiveTab] = useState('conversion'); // 'conversion' or 'revenue'
  
  // 전환율 검정용 입력값
  const [conversionInputs, setConversionInputs] = useState({
    visitorsA: '',
    conversionsA: '',
    visitorsB: '',
    conversionsB: '',
    confidenceLevel: 95
  });

  // 객단가 검정용 입력값
  const [revenueInputs, setRevenueInputs] = useState({
    visitorsA: '',
    purchasersA: '',
    revenueA: '',
    visitorsB: '',
    purchasersB: '',
    revenueB: '',
    confidenceLevel: 95
  });

  const [conversionResult, setConversionResult] = useState(null);
  const [arpuResult, setArpuResult] = useState(null);
  const [aovResult, setAovResult] = useState(null);
  const [showTooltip, setShowTooltip] = useState('');

  // 표준정규분포 CDF 근사 (오차함수 사용)
  const normalCDF = (x) => {
    return 0.5 * (1 + erf(x / Math.sqrt(2)));
  };

  const erf = (x) => {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  // 전환율 검정 (Z-test)
  const calculateConversionSignificance = () => {
    const vA = parseInt(conversionInputs.visitorsA) || 0;
    const cA = parseInt(conversionInputs.conversionsA) || 0;
    const vB = parseInt(conversionInputs.visitorsB) || 0;
    const cB = parseInt(conversionInputs.conversionsB) || 0;

    if (vA === 0 || vB === 0 || cA > vA || cB > vB || cA < 0 || cB < 0) return null;

    const rateA = cA / vA;
    const rateB = cB / vB;
    const pooledRate = (cA + cB) / (vA + vB);

    const se = Math.sqrt(pooledRate * (1 - pooledRate) * (1 / vA + 1 / vB));

    if (se === 0) return null;

    const zScore = (rateB - rateA) / se;
    const pValueTwoTailed = 2 * (1 - normalCDF(Math.abs(zScore)));

    const alpha = (100 - conversionInputs.confidenceLevel) / 100;
    const isSignificant = pValueTwoTailed < alpha;

    const improvement = rateA > 0 ? ((rateB - rateA) / rateA) * 100 : 0;
    const absoluteDifference = (rateB - rateA) * 100;
    const testConfidence = (1 - pValueTwoTailed) * 100;

    return {
      rateA: (rateA * 100).toFixed(2),
      rateB: (rateB * 100).toFixed(2),
      improvement: improvement.toFixed(1),
      absoluteDifference: absoluteDifference.toFixed(2),
      pValue: pValueTwoTailed.toFixed(4),
      testConfidence: testConfidence.toFixed(1),
      isSignificant,
      winner: rateB > rateA ? 'B' : rateA > rateB ? 'A' : 'tie',
      sampleSizeA: vA,
      sampleSizeB: vB
    };
  };

  // ARPU 검정 (방문자당 매출)
  const calculateARPUSignificance = () => {
    const vA = parseInt(revenueInputs.visitorsA) || 0;
    const revA = parseFloat(revenueInputs.revenueA) || 0;
    const vB = parseInt(revenueInputs.visitorsB) || 0;
    const revB = parseFloat(revenueInputs.revenueB) || 0;

    if (vA === 0 || vB === 0 || revA < 0 || revB < 0) return null;

    const arpuA = revA / vA;
    const arpuB = revB / vB;

    // 표준편차 추정
    const stdA = Math.sqrt(arpuA * revA / vA);
    const stdB = Math.sqrt(arpuB * revB / vB);

    // Welch's t-test
    const seA = stdA * stdA / vA;
    const seB = stdB * stdB / vB;
    const seDiff = Math.sqrt(seA + seB);

    if (seDiff === 0) return null;

    const tStat = (arpuB - arpuA) / seDiff;

    // 자유도
    const df = Math.pow(seA + seB, 2) / (Math.pow(seA, 2) / (vA - 1) + Math.pow(seB, 2) / (vB - 1));

    // p-value 계산
    const pValue = 2 * (1 - normalCDF(Math.abs(tStat)));

    const alpha = (100 - revenueInputs.confidenceLevel) / 100;
    const isSignificant = pValue < alpha;

    const improvement = arpuA > 0 ? ((arpuB - arpuA) / arpuA) * 100 : 0;
    const absoluteDifference = arpuB - arpuA;
    const testConfidence = (1 - pValue) * 100;

    return {
      valueA: arpuA.toFixed(0),
      valueB: arpuB.toFixed(0),
      improvement: improvement.toFixed(1),
      absoluteDifference: absoluteDifference.toFixed(0),
      pValue: pValue.toFixed(4),
      testConfidence: testConfidence.toFixed(1),
      isSignificant,
      winner: arpuB > arpuA ? 'B' : arpuA > arpuB ? 'A' : 'tie'
    };
  };

  // AOV 검정 (객단가 - 구매자당 평균 구매액)
  const calculateAOVSignificance = () => {
    const pA = parseInt(revenueInputs.purchasersA) || 0;
    const revA = parseFloat(revenueInputs.revenueA) || 0;
    const pB = parseInt(revenueInputs.purchasersB) || 0;
    const revB = parseFloat(revenueInputs.revenueB) || 0;

    if (pA === 0 || pB === 0 || revA < 0 || revB < 0) return null;

    const aovA = revA / pA;
    const aovB = revB / pB;

    // 표준편차 추정 (간단한 근사: 객단가의 50% 변동 가정)
    const cvAssumed = 0.5;
    const stdA = aovA * cvAssumed;
    const stdB = aovB * cvAssumed;

    // Welch's t-test
    const seA = stdA * stdA / pA;
    const seB = stdB * stdB / pB;
    const seDiff = Math.sqrt(seA + seB);

    if (seDiff === 0) return null;

    const tStat = (aovB - aovA) / seDiff;

    // 자유도
    const df = Math.pow(seA + seB, 2) / (Math.pow(seA, 2) / (pA - 1) + Math.pow(seB, 2) / (pB - 1));

    // p-value 계산
    const pValue = 2 * (1 - normalCDF(Math.abs(tStat)));

    const alpha = (100 - revenueInputs.confidenceLevel) / 100;
    const isSignificant = pValue < alpha;

    const improvement = aovA > 0 ? ((aovB - aovA) / aovA) * 100 : 0;
    const absoluteDifference = aovB - aovA;
    const testConfidence = (1 - pValue) * 100;

    return {
      valueA: aovA.toFixed(0),
      valueB: aovB.toFixed(0),
      improvement: improvement.toFixed(1),
      absoluteDifference: absoluteDifference.toFixed(0),
      pValue: pValue.toFixed(4),
      testConfidence: testConfidence.toFixed(1),
      isSignificant,
      winner: aovB > aovA ? 'B' : aovA > aovB ? 'A' : 'tie'
    };
  };

  // 입력값 변경 핸들러
  const handleConversionInputChange = (field, value) => {
    setConversionInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRevenueInputChange = (field, value) => {
    setRevenueInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 실시간 계산
  useEffect(() => {
    const calculated = calculateConversionSignificance();
    setConversionResult(calculated);
  }, [conversionInputs]);

  useEffect(() => {
    const arpuCalc = calculateARPUSignificance();
    const aovCalc = calculateAOVSignificance();
    setArpuResult(arpuCalc);
    setAovResult(aovCalc);
  }, [revenueInputs]);

  // 입력 검증
  const isValidConversionInput = () => {
    const vA = parseInt(conversionInputs.visitorsA) || 0;
    const cA = parseInt(conversionInputs.conversionsA) || 0;
    const vB = parseInt(conversionInputs.visitorsB) || 0;
    const cB = parseInt(conversionInputs.conversionsB) || 0;

    return vA > 0 && vB > 0 && cA >= 0 && cB >= 0 && cA <= vA && cB <= vB;
  };

  const isValidRevenueInput = () => {
    const vA = parseInt(revenueInputs.visitorsA) || 0;
    const pA = parseInt(revenueInputs.purchasersA) || 0;
    const revA = parseFloat(revenueInputs.revenueA) || 0;
    const vB = parseInt(revenueInputs.visitorsB) || 0;
    const pB = parseInt(revenueInputs.purchasersB) || 0;
    const revB = parseFloat(revenueInputs.revenueB) || 0;

    return vA > 0 && vB > 0 && pA > 0 && pB > 0 && revA >= 0 && revB >= 0 && pA <= vA && pB <= vB;
  };

  // 결과 카드 컴포넌트
  const ResultCard = ({ title, icon, result, metric, unit }) => (
    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-2 border-green-200">
      <div className="flex items-center mb-4">
        <div className="w-10 h-10 bg-green-200 rounded-xl flex items-center justify-center mr-3">
          {icon}
        </div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      </div>

      {result ? (
        <div className="space-y-4">
          {/* 유의성 판정 */}
          <div className={`${result.isSignificant ? 'bg-green-600' : 'bg-gray-600'} rounded-xl p-4 text-white text-center`}>
            <div className="flex items-center justify-center mb-2">
              {result.isSignificant ? (
                <Award className="w-6 h-6 mr-2" />
              ) : (
                <AlertCircle className="w-6 h-6 mr-2" />
              )}
              <span className="font-bold">
                {result.isSignificant ? '유의미한 차이' : '차이 불명확'}
              </span>
            </div>
            <p className="text-sm">
              신뢰도: {result.testConfidence}%
            </p>
          </div>

          {/* 지표 비교 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/60 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-600 mb-1">A 그룹</div>
              <div className="text-lg font-bold text-gray-900">{parseInt(result.valueA).toLocaleString()}{unit}</div>
            </div>
            <div className="bg-white/60 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-600 mb-1">B 그룹</div>
              <div className="text-lg font-bold text-gray-900">{parseInt(result.valueB).toLocaleString()}{unit}</div>
            </div>
          </div>

          {/* 통계 */}
          <div className="bg-white/60 rounded-xl p-3">
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">개선율</span>
                <span className={`font-medium ${parseFloat(result.improvement) > 0 ? 'text-green-600' : parseFloat(result.improvement) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {parseFloat(result.improvement) > 0 ? '+' : ''}{result.improvement}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">승자</span>
                <span className="font-medium">
                  {result.winner === 'tie' ? '무승부' : `${result.winner} 그룹`}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-6">
          <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">데이터를 입력해주세요</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white overflow-hidden">
      {/* 배경 장식 요소 */}
      <div className="absolute top-0 right-0 -z-10 opacity-30">
        <svg width="800" height="800" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="400" cy="400" r="400" fill="url(#radialGreen)" fillOpacity="0.3" />
          <defs>
            <radialGradient id="radialGreen" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(400 400) rotate(90) scale(400)">
              <stop stopColor="#16A34A" />
              <stop offset="1" stopColor="#16A34A" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      <Header />

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-white -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/tools"
            className="inline-flex items-center text-green-600 hover:text-green-700 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            전체 도구로 돌아가기
          </Link>

          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-6">
              <CheckCircle className="w-4 h-4 mr-2" />
              A/B 테스트 결과 판별기
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              테스트 결과가 <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-emerald-600">진짜 효과인지</span><br />
              명확하게 판별하기
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              테스트 결과가 진짜 효과인지, 단순한 우연인지 명확하게 판별해드립니다.<br />
              전환율, 방문자당 매출, 객단가를 기준으로 통계적 유의성을 검증하세요.
            </p>
            <div className="flex justify-center mb-8">
              <ShareButton />
            </div>
          </div>
        </div>
      </section>

      {/* 계산기 메인 섹션 */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 탭 메뉴 */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('conversion')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'conversion'
                    ? 'bg-white text-green-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  전환율
                </div>
              </button>
              <button
                onClick={() => setActiveTab('revenue')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'revenue'
                    ? 'bg-white text-green-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  매출·객단가
                </div>
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* 전환율 검정 입력 폼 */}
            {activeTab === 'conversion' && (
              <>
                <div className="bg-white rounded-2xl shadow-lg border border-green-200 p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">전환율 데이터 입력</h2>
                  </div>

                  <div className="space-y-8">
                    {/* A 그룹 데이터 */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">A 그룹 (기존 버전)</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center mb-2 relative">
                            <div
                              className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                              onMouseEnter={() => setShowTooltip('visitorsA')}
                              onMouseLeave={() => setShowTooltip('')}
                            >
                              <label className="block text-sm font-medium text-gray-700">
                                방문자 수
                              </label>
                              <Info className="w-4 h-4 ml-2 text-gray-400" />
                            </div>
                            {showTooltip === 'visitorsA' && (
                              <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                A 그룹에 참여한 총 방문자 수
                              </div>
                            )}
                          </div>
                          <input
                            type="number"
                            min="0"
                            placeholder="예: 1000"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={conversionInputs.visitorsA}
                            onChange={(e) => handleConversionInputChange('visitorsA', e.target.value)}
                          />
                        </div>
                        <div>
                          <div className="flex items-center mb-2 relative">
                            <div
                              className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                              onMouseEnter={() => setShowTooltip('conversionsA')}
                              onMouseLeave={() => setShowTooltip('')}
                            >
                              <label className="block text-sm font-medium text-gray-700">
                                전환 수
                              </label>
                              <Info className="w-4 h-4 ml-2 text-gray-400" />
                            </div>
                            {showTooltip === 'conversionsA' && (
                              <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                A 그룹에서 전환한 사용자 수
                              </div>
                            )}
                          </div>
                          <input
                            type="number"
                            min="0"
                            placeholder="예: 25"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={conversionInputs.conversionsA}
                            onChange={(e) => handleConversionInputChange('conversionsA', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* B 그룹 데이터 */}
                    <div className="bg-blue-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">B 그룹 (새로운 버전)</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center mb-2 relative">
                            <div
                              className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                              onMouseEnter={() => setShowTooltip('visitorsB')}
                              onMouseLeave={() => setShowTooltip('')}
                            >
                              <label className="block text-sm font-medium text-gray-700">
                                방문자 수
                              </label>
                              <Info className="w-4 h-4 ml-2 text-gray-400" />
                            </div>
                            {showTooltip === 'visitorsB' && (
                              <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                B 그룹에 참여한 총 방문자 수
                              </div>
                            )}
                          </div>
                          <input
                            type="number"
                            min="0"
                            placeholder="예: 1000"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={conversionInputs.visitorsB}
                            onChange={(e) => handleConversionInputChange('visitorsB', e.target.value)}
                          />
                        </div>
                        <div>
                          <div className="flex items-center mb-2 relative">
                            <div
                              className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                              onMouseEnter={() => setShowTooltip('conversionsB')}
                              onMouseLeave={() => setShowTooltip('')}
                            >
                              <label className="block text-sm font-medium text-gray-700">
                                전환 수
                              </label>
                              <Info className="w-4 h-4 ml-2 text-gray-400" />
                            </div>
                            {showTooltip === 'conversionsB' && (
                              <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                B 그룹에서 전환한 사용자 수
                              </div>
                            )}
                          </div>
                          <input
                            type="number"
                            min="0"
                            placeholder="예: 35"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={conversionInputs.conversionsB}
                            onChange={(e) => handleConversionInputChange('conversionsB', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 신뢰도 수준 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        신뢰도 수준
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        value={conversionInputs.confidenceLevel}
                        onChange={(e) => handleConversionInputChange('confidenceLevel', parseInt(e.target.value))}
                      >
                        <option value={90}>90%</option>
                        <option value={95}>95% (권장)</option>
                        <option value={99}>99%</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* 전환율 결과 표시 */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border-2 border-green-200">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-green-200 rounded-xl flex items-center justify-center mr-4">
                        <BarChart3 className="w-6 h-6 text-green-700" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">판별 결과</h2>
                    </div>

                    {isValidConversionInput() && conversionResult ? (
                      <div className="space-y-6">
                        {/* 유의성 판정 */}
                        <div className={`${conversionResult.isSignificant ? 'bg-green-600' : 'bg-gray-600'} rounded-xl p-6 text-white text-center`}>
                          <div className="flex items-center justify-center mb-3">
                            {conversionResult.isSignificant ? (
                              <Award className="w-8 h-8 mr-3" />
                            ) : (
                              <AlertCircle className="w-8 h-8 mr-3" />
                            )}
                            <h3 className="text-xl font-bold">
                              {conversionResult.isSignificant ? '진짜 효과입니다' : '우연일 가능성이 높습니다'}
                            </h3>
                          </div>
                          <p className="text-lg">
                            {conversionResult.isSignificant
                              ? `${conversionInputs.confidenceLevel}% 신뢰도로 실제 차이가 존재합니다`
                              : `${conversionInputs.confidenceLevel}% 신뢰도로 차이를 확인할 수 없습니다`
                            }
                          </p>
                        </div>

                        {/* 전환율 비교 */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white/60 rounded-xl p-4 text-center">
                            <div className="text-sm text-gray-600 mb-1">A 그룹 전환율</div>
                            <div className="text-2xl font-bold text-gray-900">{conversionResult.rateA}%</div>
                            <div className="text-xs text-gray-500">{conversionResult.sampleSizeA}명 중 {conversionInputs.conversionsA}명</div>
                          </div>
                          <div className="bg-white/60 rounded-xl p-4 text-center">
                            <div className="text-sm text-gray-600 mb-1">B 그룹 전환율</div>
                            <div className="text-2xl font-bold text-gray-900">{conversionResult.rateB}%</div>
                            <div className="text-xs text-gray-500">{conversionResult.sampleSizeB}명 중 {conversionInputs.conversionsB}명</div>
                          </div>
                        </div>

                        {/* 승부 결과 */}
                        <div className="bg-white/60 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-4 text-center">승부 결과</h4>
                          {conversionResult.winner !== 'tie' ? (
                            <div className="text-center">
                              <div className={`text-3xl font-bold mb-2 ${conversionResult.winner === 'B' ? 'text-blue-600' : 'text-gray-600'}`}>
                                {conversionResult.winner === 'B' ? 'B 그룹 승리' : 'A 그룹 승리'}
                              </div>
                              <div className="text-sm text-gray-600">
                                {conversionResult.winner === 'B' ? '새로운 버전' : '기존 버전'}이 더 좋은 성과를 보였습니다
                              </div>
                            </div>
                          ) : (
                            <div className="text-center">
                              <div className="text-2xl font-bold text-gray-600 mb-2">무승부</div>
                              <div className="text-sm text-gray-600">두 그룹의 성과가 비슷합니다</div>
                            </div>
                          )}
                        </div>

                        {/* 통계 정보 */}
                        <div className="bg-white/60 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-3">상세 통계</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">상대적 개선율</span>
                              <span className={`font-medium ${parseFloat(conversionResult.improvement) > 0 ? 'text-green-600' : parseFloat(conversionResult.improvement) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {parseFloat(conversionResult.improvement) > 0 ? '+' : ''}{conversionResult.improvement}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">전환율 차이</span>
                              <span className="font-medium">{parseFloat(conversionResult.absoluteDifference) > 0 ? '+' : ''}{conversionResult.absoluteDifference}%p</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">결과 신뢰도</span>
                              <span className={`font-medium ${conversionResult.isSignificant ? 'text-green-600' : 'text-red-600'}`}>
                                {conversionResult.testConfidence}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">
                          {!conversionInputs.visitorsA || !conversionInputs.visitorsB ? '각 그룹의 방문자 수를 입력해주세요' :
                            parseInt(conversionInputs.conversionsA) > parseInt(conversionInputs.visitorsA) || parseInt(conversionInputs.conversionsB) > parseInt(conversionInputs.visitorsB) ? '전환 수는 방문자 수보다 클 수 없습니다' :
                              '올바른 값을 입력해주세요'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* 해석 가이드 */}
                  {isValidConversionInput() && conversionResult && (
                    <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                      <div className="flex items-start">
                        <CheckCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-blue-900 mb-2">결과 해석</h3>
                          <div className="text-sm text-blue-800 space-y-2">
                            {conversionResult.isSignificant ? (
                              <>
                                <p>
                                  • <strong>진짜 효과</strong>입니다. 결과 신뢰도가 {conversionResult.testConfidence}%로 높습니다.
                                </p>
                                <p>
                                  • 그룹 {conversionResult.winner}가 {Math.abs(parseFloat(conversionResult.improvement)).toFixed(1)}% 더 좋은 성과를 보였습니다.
                                </p>
                                <p>
                                  • 이 결과를 바탕으로 의사결정을 내려도 안전합니다.
                                </p>
                              </>
                            ) : (
                              <>
                                <p>
                                  • <strong>우연일 가능성이 높습니다</strong>. 실제 차이가 아닐 수 있습니다.
                                </p>
                                <p>
                                  • 더 많은 데이터를 수집하거나 테스트를 계속 진행하는 것을 권장합니다.
                                </p>
                                <p>
                                  • 현재 결과만으로는 확실한 결론을 내리기 어렵습니다.
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* 매출·객단가 검정 입력 폼 */}
            {activeTab === 'revenue' && (
              <>
                <div className="bg-white rounded-2xl shadow-lg border border-green-200 p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">매출 데이터 입력</h2>
                  </div>

                  <div className="space-y-8">
                    {/* A 그룹 데이터 */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">A 그룹 (기존 버전)</h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center mb-2 relative">
                              <div
                                className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                                onMouseEnter={() => setShowTooltip('revenueVisitorsA')}
                                onMouseLeave={() => setShowTooltip('')}
                              >
                                <label className="block text-sm font-medium text-gray-700">
                                  방문자 수
                                </label>
                                <Info className="w-4 h-4 ml-2 text-gray-400" />
                              </div>
                              {showTooltip === 'revenueVisitorsA' && (
                                <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                  A 그룹에 참여한 총 방문자 수
                                </div>
                              )}
                            </div>
                            <input
                              type="number"
                              min="0"
                              placeholder="예: 1000"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              value={revenueInputs.visitorsA}
                              onChange={(e) => handleRevenueInputChange('visitorsA', e.target.value)}
                            />
                          </div>
                          <div>
                            <div className="flex items-center mb-2 relative">
                              <div
                                className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                                onMouseEnter={() => setShowTooltip('purchasersA')}
                                onMouseLeave={() => setShowTooltip('')}
                              >
                                <label className="block text-sm font-medium text-gray-700">
                                  구매 고객 수
                                </label>
                                <Info className="w-4 h-4 ml-2 text-gray-400" />
                              </div>
                              {showTooltip === 'purchasersA' && (
                                <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                  실제로 구매한 고객 수
                                </div>
                              )}
                            </div>
                            <input
                              type="number"
                              min="0"
                              placeholder="예: 30"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              value={revenueInputs.purchasersA}
                              onChange={(e) => handleRevenueInputChange('purchasersA', e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center mb-2 relative">
                            <div
                              className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                              onMouseEnter={() => setShowTooltip('revenueA')}
                              onMouseLeave={() => setShowTooltip('')}
                            >
                              <label className="block text-sm font-medium text-gray-700">
                                총 매출액 (원)
                              </label>
                              <Info className="w-4 h-4 ml-2 text-gray-400" />
                            </div>
                            {showTooltip === 'revenueA' && (
                              <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                A 그룹에서 발생한 총 매출액
                              </div>
                            )}
                          </div>
                          <input
                            type="number"
                            min="0"
                            placeholder="예: 1500000"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={revenueInputs.revenueA}
                            onChange={(e) => handleRevenueInputChange('revenueA', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* B 그룹 데이터 */}
                    <div className="bg-blue-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">B 그룹 (새로운 버전)</h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center mb-2 relative">
                              <div
                                className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                                onMouseEnter={() => setShowTooltip('revenueVisitorsB')}
                                onMouseLeave={() => setShowTooltip('')}
                              >
                                <label className="block text-sm font-medium text-gray-700">
                                  방문자 수
                                </label>
                                <Info className="w-4 h-4 ml-2 text-gray-400" />
                              </div>
                              {showTooltip === 'revenueVisitorsB' && (
                                <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                  B 그룹에 참여한 총 방문자 수
                                </div>
                              )}
                            </div>
                            <input
                              type="number"
                              min="0"
                              placeholder="예: 1000"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              value={revenueInputs.visitorsB}
                              onChange={(e) => handleRevenueInputChange('visitorsB', e.target.value)}
                            />
                          </div>
                          <div>
                            <div className="flex items-center mb-2 relative">
                              <div
                                className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                                onMouseEnter={() => setShowTooltip('purchasersB')}
                                onMouseLeave={() => setShowTooltip('')}
                              >
                                <label className="block text-sm font-medium text-gray-700">
                                  구매 고객 수
                                </label>
                                <Info className="w-4 h-4 ml-2 text-gray-400" />
                              </div>
                              {showTooltip === 'purchasersB' && (
                                <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                  실제로 구매한 고객 수
                                </div>
                              )}
                            </div>
                            <input
                              type="number"
                              min="0"
                              placeholder="예: 40"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              value={revenueInputs.purchasersB}
                              onChange={(e) => handleRevenueInputChange('purchasersB', e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center mb-2 relative">
                            <div
                              className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                              onMouseEnter={() => setShowTooltip('revenueB')}
                              onMouseLeave={() => setShowTooltip('')}
                            >
                              <label className="block text-sm font-medium text-gray-700">
                                총 매출액 (원)
                              </label>
                              <Info className="w-4 h-4 ml-2 text-gray-400" />
                            </div>
                            {showTooltip === 'revenueB' && (
                              <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                                B 그룹에서 발생한 총 매출액
                              </div>
                            )}
                          </div>
                          <input
                            type="number"
                            min="0"
                            placeholder="예: 2400000"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={revenueInputs.revenueB}
                            onChange={(e) => handleRevenueInputChange('revenueB', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 신뢰도 수준 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        신뢰도 수준
                      </label>
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        value={revenueInputs.confidenceLevel}
                        onChange={(e) => handleRevenueInputChange('confidenceLevel', parseInt(e.target.value))}
                      >
                        <option value={90}>90%</option>
                        <option value={95}>95% (권장)</option>
                        <option value={99}>99%</option>
                      </select>
                    </div>

                    {/* 안내 메시지 */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-start">
                        <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">두 가지 지표를 동시에 검정합니다</p>
                          <p><strong>ARPU</strong>: 방문자당 평균 매출 (총 매출 / 방문자)</p>
                          <p><strong>AOV</strong>: 객단가 (총 매출 / 구매 고객 수)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 매출·객단가 결과 표시 */}
                <div className="space-y-6">
                  {isValidRevenueInput() ? (
                    <>
                      {/* ARPU 결과 */}
                      <ResultCard
                        title="방문자당 매출 (ARPU)"
                        icon={<Users className="w-6 h-6 text-green-600" />}
                        result={arpuResult}
                        metric="ARPU"
                        unit="원"
                      />

                      {/* AOV 결과 */}
                      <ResultCard
                        title="객단가 (AOV)"
                        icon={<ShoppingCart className="w-6 h-6 text-green-600" />}
                        result={aovResult}
                        metric="AOV"
                        unit="원"
                      />

                      {/* 종합 해석 가이드 */}
                      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                        <div className="flex items-start">
                          <CheckCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
                          <div>
                            <h3 className="font-semibold text-blue-900 mb-2">종합 해석</h3>
                            <div className="text-sm text-blue-800 space-y-2">
                              {arpuResult && aovResult && (
                                <>
                                  {arpuResult.isSignificant && aovResult.isSignificant ? (
                                    <>
                                      <p>• <strong>두 지표 모두 유의미합니다!</strong></p>
                                      <p>• ARPU 승자: {arpuResult.winner === 'tie' ? '무승부' : `${arpuResult.winner} 그룹`}</p>
                                      <p>• AOV 승자: {aovResult.winner === 'tie' ? '무승부' : `${aovResult.winner} 그룹`}</p>
                                      {arpuResult.winner === aovResult.winner ? (
                                        <p>• 두 지표 모두 {arpuResult.winner} 그룹이 우세합니다. 명확한 선택입니다!</p>
                                      ) : (
                                        <p>• 두 지표의 승자가 다릅니다. 비즈니스 전략에 따라 선택하세요.</p>
                                      )}
                                    </>
                                  ) : arpuResult.isSignificant ? (
                                    <>
                                      <p>• <strong>ARPU는 유의미</strong>하지만 AOV는 불명확합니다.</p>
                                      <p>• 방문자당 매출 관점에서 {arpuResult.winner} 그룹이 우세합니다.</p>
                                    </>
                                  ) : aovResult.isSignificant ? (
                                    <>
                                      <p>• <strong>AOV는 유의미</strong>하지만 ARPU는 불명확합니다.</p>
                                      <p>• 객단가 관점에서 {aovResult.winner} 그룹이 우세합니다.</p>
                                    </>
                                  ) : (
                                    <>
                                      <p>• <strong>두 지표 모두 불명확합니다.</strong></p>
                                      <p>• 더 많은 데이터를 수집하거나 테스트를 계속 진행하세요.</p>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border-2 border-green-200">
                      <div className="text-center py-8">
                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">
                          각 그룹의 방문자 수, 구매 고객 수, 총 매출액을 입력해주세요
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* 다른 도구 추천 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">다음 단계 도구</h2>
            <p className="text-xl text-gray-600">결과 판별 후 이어서 사용할 수 있는 도구들입니다</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-sm border-2 border-yellow-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💰</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">전환율 개선 효과 계산기</h3>
              </div>
              <p className="text-gray-600 mb-6">개선된 전환율로 예상되는 매출 증가를 계산합니다.</p>
              <Link
                to="/tools/effect"
                className="block w-full bg-gradient-to-r from-yellow-600 to-yellow-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                효과 계산하기
              </Link>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border-2 border-red-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💵</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">ROI(순수익) 계산기</h3>
              </div>
              <p className="text-gray-600 mb-6">판매가와 순이익을 기반으로 실제 수익을 계산합니다.</p>
              <Link
                to="/tools/roi"
                className="block w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                ROI 계산하기
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            지금 바로 전환율 최적화를 시작하세요
          </h2>
          <p className="text-xl mb-10 text-blue-100">
            SWITCHUP으로 직관적이고 편리하게 A/B 테스트를 실행하고 전환율을 개선하세요
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-10">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                { label: "직관적인 인터페이스", description: "복잡한 설정 없이 쉽게 시작" },
                { label: "30일 무료 체험", description: "부담 없이 모든 기능 사용" },
                { label: "즉시 결과 확인", description: "실시간 데이터와 분석 제공" }
              ].map((item, index) => (
                <div key={index}>
                  <div className="text-xl font-bold mb-2">{item.label}</div>
                  <div className="text-blue-100">{item.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/freestart"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
            >
              무료로 시작하기
            </Link>
            <Link
              to="/service/ab-test"
              className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300 font-medium"
            >
              SWITCHUP 서비스 보기
            </Link>
          </div>
        </div>
      </section>

      {/* 카카오 문의하기 버튼 */}
      {/* <SimpleKakaoButton
              channelId="_yixgxjn"
              buttonText="카카오로 문의하기"
              mobileText="카카오 문의"
            /> */}

      <Footer />
    </div>
  );
};

export default SignificanceCalculator;